import { IconButton, Popover, Typography } from '@mui/material'
import {
    green,
    marketStatusCloseBg,
    marketStatusOpenBg,
    preMarketOpenBg,
    preMarketTextColor,
    red
} from '../../assets/colors'
import Morning from '../../assets/icons/Morning'
import MarketOpen from '../../assets/icons/MarketOpen'
import MarketClosed from '../../assets/icons/MarketClosed'
import { ExchangeTradingSession, TradingSession } from '../../proto/generated/trading-calendar'
import MarketStatusBar from './Slider'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectPrimarySecurity } from '../../selectors/home'
import { NonInteractiveTooltip } from '../../components/custom'
import { selectExchangeTradingSessions } from '../../selectors/chart'

const MarketStatus = () => {
    const { expressionMode, primary, underlying, expressionSecurities } = useSelector(selectPrimarySecurity)
    const mainSecurity = expressionMode ? expressionSecurities[0] : (underlying || primary)
    const exchangeTradingSessions = useSelector(selectExchangeTradingSessions)
    const [marketOpen, setMarketOpen] = useState<boolean>()
    const [preMarketOpen, setPreMarketOpen] = useState<boolean>()
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
    const [message, setMessage] = useState<any>()
    const [currentTradingSession, setCurrentTradingSession] = useState<ExchangeTradingSession>()
    const open = Boolean(anchorEl)
    const [updatedKey, setUpdatedKey] = useState<number>(0)

    const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    useEffect(() => {
        if (!exchangeTradingSessions || !exchangeTradingSessions.length || !mainSecurity) return
        const exchangeTradingSession = exchangeTradingSessions.find((exchangeTradingSession: ExchangeTradingSession) => {
            return exchangeTradingSession.exchange === mainSecurity.exchange
        })
        const timeoutIds: NodeJS.Timeout[] = []
        const nowTime = Date.now()
        setCurrentTradingSession(exchangeTradingSession)
        if (exchangeTradingSession) {
            for (let i = 0; i < exchangeTradingSession.tradingSessions.length; i++) {
                const tradingSession = exchangeTradingSession.tradingSessions[i]
                if(nowTime<tradingSession.preOpenStartTime) {
                    const diff = tradingSession.preOpenStartTime - nowTime
                    const id = setTimeout(() => {
                        setUpdatedKey(prevKey => prevKey + 1)
                    }, diff)
                    timeoutIds.push(id)
                }
                if(nowTime<tradingSession.preOpenEndTime) {
                    const diff = tradingSession.preOpenEndTime - nowTime
                    const id = setTimeout(() => {
                        setUpdatedKey(prevKey => prevKey + 1)
                    }, diff)
                    timeoutIds.push(id)
                }
                if(nowTime<tradingSession.startTime) {
                    const diff = tradingSession.startTime - nowTime
                    const id = setTimeout(() => {
                        setUpdatedKey(prevKey => prevKey + 1)
                    }, diff)
                    timeoutIds.push(id)
                }
                if(nowTime<tradingSession.endTime) {
                    const diff = tradingSession.endTime - nowTime
                    const id = setTimeout(() => {
                        setUpdatedKey(prevKey => prevKey + 1)
                    }, diff)
                    timeoutIds.push(id)
                }
            }
        }
        return () => {
            timeoutIds.forEach(id => clearTimeout(id))
        }
    }, [exchangeTradingSessions, mainSecurity])

    const calculateTimeUntilOpen = (marketOpenStartTime: Date, currentTime: Date) => {
        const timeUntilOpen = marketOpenStartTime.getTime() - currentTime.getTime()
        const hours = Math.floor(timeUntilOpen / (1000 * 60 * 60))
        const minutes = Math.floor((timeUntilOpen % (1000 * 60 * 60)) / (1000 * 60))
        return `Market opens in ${hours > 0 ? `${hours} hours and` : ''} ${minutes} minutes.`
    }

    const calculateTimeUntilClose = (marketOpenEndTime: Date, currentTime: Date) => {
        const timeUntilClose = marketOpenEndTime.getTime() - currentTime.getTime()
        const hours = Math.floor(timeUntilClose / (1000 * 60 * 60))
        const minutes = Math.floor((timeUntilClose % (1000 * 60 * 60)) / (1000 * 60))
        return `Market closes in ${hours > 0 ? `${hours} hours and` : ''} ${minutes} minutes.`
    }

    useEffect(() => {
        if (!exchangeTradingSessions || !exchangeTradingSessions.length || !mainSecurity) return
        const exchangeTradingSession = exchangeTradingSessions.find((exchangeTradingSession: ExchangeTradingSession) => {
            return exchangeTradingSession.exchange === mainSecurity.exchange
        })
        setCurrentTradingSession(exchangeTradingSession)
        if (exchangeTradingSession) {
            const currentTime = new Date()
            let marketOpen = false
            let preMarketOpen = false
            let message = ''

            for (let i = 0; i < exchangeTradingSession.tradingSessions.length; i++) {
                const tradingSession = exchangeTradingSession.tradingSessions[i]
                const marketPreOpenStartTime = new Date(tradingSession.preOpenStartTime)
                const marketPreOpenEndTime = new Date(tradingSession.preOpenEndTime)
                const marketOpenStartTime = new Date(tradingSession.startTime)
                const marketOpenEndTime = new Date(tradingSession.endTime)

                if (currentTime >= marketPreOpenStartTime && currentTime <= marketPreOpenEndTime) {
                    preMarketOpen = true
                    message = `Morning. Market is open for pre-market trading. ${calculateTimeUntilOpen(marketOpenStartTime, currentTime)}`
                } else if (currentTime >= marketOpenStartTime && currentTime <= marketOpenEndTime) {
                    marketOpen = true
                    message = calculateTimeUntilClose(marketOpenEndTime, currentTime)
                    break
                } else {
                    const nextSessionIndex = i + 1
                    if (nextSessionIndex < exchangeTradingSession.tradingSessions.length) {
                        const nextSession = exchangeTradingSession.tradingSessions[nextSessionIndex]
                        const nextSessionOpenTime = new Date(nextSession.startTime)
                        message = calculateTimeUntilOpen(nextSessionOpenTime, currentTime)
                        break
                    } else {
                        if (currentTime < marketOpenStartTime) {
                            message = calculateTimeUntilOpen(marketOpenStartTime, currentTime)
                        }
                    }
                }
            }
            setMarketOpen(marketOpen)
            setMessage(message)
            setPreMarketOpen(preMarketOpen)
        }
    }, [exchangeTradingSessions, mainSecurity, updatedKey])

    const tooltipContent = useMemo(() => (
        <Typography
            variant="caption">{`${preMarketOpen ? 'Pre-market is open' : (marketOpen ? 'Market Open' : 'Market Closed')}`}</Typography>
    ), [preMarketOpen, marketOpen])

    const tooltipChildren = useMemo(() => {
        return <IconButton
            style={{ padding: '0px' }}
            disableRipple={true}
            data-event={'Market Status Icon Clicked'}
            data-event-source={'stockbar'}
            onClick={handleClick}>
            <div
                style={{
                    borderRadius: '50%',
                    backgroundColor: preMarketOpen ? preMarketOpenBg : marketOpen ? marketStatusOpenBg : marketStatusCloseBg
                }}>
                {preMarketOpen ? <Morning /> : (marketOpen ? <MarketOpen /> : <MarketClosed />)}
            </div>
        </IconButton>
    }, [handleClick, preMarketOpen, marketOpen])

    const popover = useMemo(() => (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            style={{ marginLeft: '-8px', marginTop: '8px' }}
            slotProps={{
                paper: {
                    style: { borderRadius: '6px' }
                }
            }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
            }}
        >
            <div style={{ maxWidth: '284px' }}>
                <div style={{
                    display: 'flex',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    paddingLeft: '16px',
                    paddingRight: '16px'
                }}>
                    <div
                        style={{
                            alignSelf: 'flex-start',
                            padding: '0px',
                            borderRadius: '50%',
                            backgroundColor: preMarketOpen ? preMarketOpenBg : marketOpen ? marketStatusOpenBg : marketStatusCloseBg,
                            marginTop: '4px'
                        }}>
                        {preMarketOpen ? <Morning /> : (marketOpen ? <MarketOpen /> : <MarketClosed />)}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '8px' }}>
                        <Typography variant="body1"
                                    style={{
                                        fontWeight: 600,
                                        color: preMarketOpen ? preMarketTextColor : marketOpen ? green : red
                                    }}>{preMarketOpen ? 'Pre-market' : marketOpen ? 'Market Open' : 'Market Closed'}</Typography>
                        <Typography
                            variant="body2">{`${preMarketOpen ? '' : (marketOpen ? 'Don\'t miss out on opportunities' : 'Market doors closed for the day')}`}
                        </Typography>
                        <Typography variant="body2"
                                    sx={{ fontWeight: '600' }}>{message}</Typography>
                        <div style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            marginTop: '16px'
                        }}>
                            {
                                currentTradingSession && currentTradingSession.tradingSessions.map((tradingSession: TradingSession) => {
                                    return (
                                        <MarketStatusBar key={tradingSession.name}
                                                         tradingSession={tradingSession} />
                                    )
                                })
                            }
                        </div>
                    </div>
                </div>
            </div>
        </Popover>
    ), [open, anchorEl, handleClose, preMarketOpen, marketOpen, message, currentTradingSession])

    return (<>
        <NonInteractiveTooltip title={tooltipContent} arrow={true} placement="top">
            {tooltipChildren}
        </NonInteractiveTooltip>
        {popover}
    </>)
}
export default memo(MarketStatus)